import { useCallback, useEffect, useRef } from 'react'
import useSWR from 'swr'
import { audienceImportSessionsApi } from '@/lib/api'
import type { AudienceCreationFlowSnapshot } from '@digital-twins/shared-types'
import type { AudienceImportWorkbookProjection } from '@/types/components/audiences/import-session'
import type { ImportSessionUpdatedEvent } from '@/lib/services/websocket'
import {
  useImportWizardStableContext,
} from '../context/ImportWizardContext'
import { getAudienceImportWorkbookProjectionKey } from '../lib/cache-keys'
import { incrementImportSessionClientMetric } from '../lib/import-session-client-metrics'
import { legacyRefreshInterval } from '@/lib/realtime/realtimeKillSwitch'
import { shouldRevalidateWorkbookProjectionForImportSessionUpdate } from '../lib/import-session-update-events'
import {
  getRouteAckFromImportSessionEvent,
  mergeRouteAckIntoWorkbookProjection,
} from '../lib/import-session-deltas'

interface UseWorkbookProjectionOptions {
  lastMutationRequestId?: string | null
  flowSnapshot?: AudienceCreationFlowSnapshot | null
}

export function useWorkbookProjection(
  sessionId: string,
  _options: UseWorkbookProjectionOptions = {},
) {
  const { registerImportSessionEventHandler } = useImportWizardStableContext()

  const key = getAudienceImportWorkbookProjectionKey(sessionId)
  const query = useSWR<AudienceImportWorkbookProjection | null>(
    key,
    sessionId
      ? (_key: unknown, options?: { signal?: AbortSignal }) =>
          audienceImportSessionsApi.getWorkbookProjection(sessionId, options?.signal)
      : null,
    {
      revalidateOnFocus: false,
      dedupingInterval: 5000,
      refreshInterval: legacyRefreshInterval('IMPORT', 1500),
    },
  )

  const revalidateProjection = useCallback(
    async (source: string) => {
      const start = performance.now()
      try {
        await query.mutate()
      } finally {
        incrementImportSessionClientMetric('workbook_projection_revalidate', {
          sessionId,
          hook: 'workbook',
          source,
          durationMs: Math.round(performance.now() - start),
        })
      }
    },
    [query, sessionId],
  )

  const handlerRef = useRef<(event: ImportSessionUpdatedEvent) => void>(() => {})
  handlerRef.current = (event: ImportSessionUpdatedEvent) => {
    if (!shouldRevalidateWorkbookProjectionForImportSessionUpdate(event)) {
      incrementImportSessionClientMetric('workbook_projection_revalidate_skipped', {
        sessionId,
        hook: 'workbook',
        source: event.reason,
      })
      return
    }
    const ack = getRouteAckFromImportSessionEvent(event)
    if (ack) {
      const merged = mergeRouteAckIntoWorkbookProjection(query.data ?? null, ack)
      if (merged !== undefined) {
        void query.mutate(merged, { revalidate: false })
        return
      }
    }
    void revalidateProjection(`websocket:${event.reason}`)
  }
  const stableHandler = useCallback((event: ImportSessionUpdatedEvent) => {
    handlerRef.current(event)
  }, [])

  useEffect(() => {
    return registerImportSessionEventHandler(stableHandler)
  }, [registerImportSessionEventHandler, stableHandler])

  return {
    projection: query.data ?? null,
    error: query.error as Error | undefined,
    isLoading: query.isLoading,
    isValidating: query.isValidating,
    mutate: query.mutate,
  }
}
